import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Trash2, Pencil, Tag, ToggleLeft, ToggleRight, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PromoCodeManager = () => {
  const [promoCodes, setPromoCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: '',
    minimum_order: 0,
    max_uses: '',
    expires_at: '',
    applicable_categories: [],
    description: ''
  });

  const fetchPromoCodes = async () => {
    try {
      const response = await axios.get(`${API}/admin/promo-codes`);
      setPromoCodes(response.data);
    } catch (error) {
      // Silent fail - promo codes list will remain empty
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPromoCodes();
  }, []);

  const resetForm = () => {
    setFormData({
      code: '',
      discount_type: 'percentage',
      discount_value: '',
      minimum_order: 0,
      max_uses: '',
      expires_at: '',
      applicable_categories: [],
      description: ''
    });
    setEditingPromo(null);
  };

  const openModal = (promo = null) => {
    if (promo) {
      setEditingPromo(promo);
      setFormData({
        code: promo.code,
        discount_type: promo.discount_type,
        discount_value: promo.discount_value,
        minimum_order: promo.minimum_order || 0,
        max_uses: promo.max_uses || '',
        expires_at: promo.expires_at ? new Date(promo.expires_at).toISOString().split('T')[0] : '',
        applicable_categories: promo.applicable_categories || [],
        description: promo.description || ''
      });
    } else {
      resetForm();
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const payload = {
      ...formData,
      discount_value: parseFloat(formData.discount_value),
      minimum_order: parseFloat(formData.minimum_order) || 0,
      max_uses: formData.max_uses ? parseInt(formData.max_uses) : null,
      expires_at: formData.expires_at ? new Date(formData.expires_at).toISOString() : null
    };

    try {
      if (editingPromo) {
        await axios.put(`${API}/admin/promo-codes/${editingPromo.id}`, payload);
        toast.success('Promo code updated');
      } else {
        await axios.post(`${API}/admin/promo-codes`, payload);
        toast.success('Promo code created');
      }
      setShowModal(false);
      resetForm();
      fetchPromoCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save promo code');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this promo code?')) return;
    
    try {
      await axios.delete(`${API}/admin/promo-codes/${id}`);
      toast.success('Promo code deleted');
      fetchPromoCodes();
    } catch (error) {
      toast.error('Failed to delete promo code');
    }
  };

  const handleToggle = async (id) => {
    try {
      const response = await axios.put(`${API}/admin/promo-codes/${id}/toggle`);
      toast.success(response.data.message);
      fetchPromoCodes();
    } catch (error) {
      toast.error('Failed to toggle promo code');
    }
  };

  const toggleCategory = (category) => {
    setFormData(prev => ({
      ...prev,
      applicable_categories: prev.applicable_categories.includes(category)
        ? prev.applicable_categories.filter(c => c !== category)
        : [...prev.applicable_categories, category]
    }));
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-10 w-48 bg-sand rounded" />
        <div className="h-64 bg-sand rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="promo-code-manager">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-xl text-text-primary">Promo Codes</h2>
          <p className="text-sm text-text-muted">Manage discount codes for your store</p>
        </div>
        <Button onClick={() => openModal()} className="rounded-full bg-forest hover:bg-forest-dark text-white">
          <Plus className="w-4 h-4 mr-2" />
          Add Promo Code
        </Button>
      </div>

      {promoCodes.length === 0 ? (
        <div className="text-center py-12 bg-sand/30 rounded-2xl">
          <Tag className="w-12 h-12 text-text-muted mx-auto mb-4" />
          <p className="text-text-muted">No promo codes yet</p>
          <Button onClick={() => openModal()} className="mt-4 rounded-full" variant="outline">
            Create your first promo code
          </Button>
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-card overflow-hidden">
          <table className="w-full">
            <thead className="bg-sand">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-text-secondary">Code</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-text-secondary">Discount</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-text-secondary">Usage</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-text-secondary">Expires</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-text-secondary">Status</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-text-secondary">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {promoCodes.map((promo) => (
                <tr key={promo.id} className={!promo.is_active ? 'opacity-50' : ''}>
                  <td className="px-4 py-3">
                    <span className="font-mono font-medium text-text-primary">{promo.code}</span>
                    {promo.description && (
                      <p className="text-xs text-text-muted mt-0.5">{promo.description}</p>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-terracotta font-medium">
                      {promo.discount_type === 'percentage' 
                        ? `${promo.discount_value}%` 
                        : `$${promo.discount_value}`}
                    </span>
                    {promo.minimum_order > 0 && (
                      <p className="text-xs text-text-muted">Min: ${promo.minimum_order}</p>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-text-primary">{promo.times_used}</span>
                    <span className="text-text-muted">
                      {promo.max_uses ? ` / ${promo.max_uses}` : ' / ∞'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-text-muted">
                    {promo.expires_at 
                      ? new Date(promo.expires_at).toLocaleDateString()
                      : 'Never'}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => handleToggle(promo.id)}
                      className="flex items-center gap-1"
                    >
                      {promo.is_active ? (
                        <>
                          <ToggleRight className="w-6 h-6 text-forest" />
                          <span className="text-xs text-forest">Active</span>
                        </>
                      ) : (
                        <>
                          <ToggleLeft className="w-6 h-6 text-text-muted" />
                          <span className="text-xs text-text-muted">Inactive</span>
                        </>
                      )}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => openModal(promo)}
                        className="p-2 text-text-muted hover:text-terracotta hover:bg-terracotta/10 rounded-lg transition-colors"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(promo.id)}
                        className="p-2 text-text-muted hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {editingPromo ? 'Edit Promo Code' : 'Create Promo Code'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Code *</Label>
              <Input
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                placeholder="e.g., WELCOME20"
                className="rounded-xl mt-1 uppercase"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Discount Type *</Label>
                <Select
                  value={formData.discount_type}
                  onValueChange={(value) => setFormData({ ...formData, discount_type: value })}
                >
                  <SelectTrigger className="rounded-xl mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percentage (%)</SelectItem>
                    <SelectItem value="fixed">Fixed Amount ($)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Discount Value *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.discount_value}
                  onChange={(e) => setFormData({ ...formData, discount_value: e.target.value })}
                  placeholder={formData.discount_type === 'percentage' ? '20' : '10.00'}
                  className="rounded-xl mt-1"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Minimum Order ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.minimum_order}
                  onChange={(e) => setFormData({ ...formData, minimum_order: e.target.value })}
                  placeholder="0"
                  className="rounded-xl mt-1"
                />
              </div>
              <div>
                <Label>Max Uses</Label>
                <Input
                  type="number"
                  value={formData.max_uses}
                  onChange={(e) => setFormData({ ...formData, max_uses: e.target.value })}
                  placeholder="Unlimited"
                  className="rounded-xl mt-1"
                />
              </div>
            </div>

            <div>
              <Label>Expires On</Label>
              <Input
                type="date"
                value={formData.expires_at}
                onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
                className="rounded-xl mt-1"
              />
            </div>

            <div>
              <Label>Applicable Categories</Label>
              <p className="text-xs text-text-muted mb-2">Leave empty for all categories</p>
              <div className="flex flex-wrap gap-2">
                {['teas', 'supplements', 'soap', 'supplies'].map((cat) => (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => toggleCategory(cat)}
                    className={`px-3 py-1.5 rounded-full text-sm capitalize transition-colors ${
                      formData.applicable_categories.includes(cat)
                        ? 'bg-terracotta text-white'
                        : 'bg-stone-100 text-text-secondary hover:bg-stone-200'
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label>Description (optional)</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="e.g., New customer discount"
                className="rounded-xl mt-1"
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="rounded-full">
                Cancel
              </Button>
              <Button type="submit" className="rounded-full bg-terracotta hover:bg-terracotta-dark text-white">
                {editingPromo ? 'Update' : 'Create'} Promo Code
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PromoCodeManager;
